import { createAsyncThunk } from '@reduxjs/toolkit';

import { fetchWrapper } from '../../../../helpers';
import { TREATMENT_PLANS_V2_API_BASE } from '../../constants';

import type {
  IClientSibling,
  IFamily,
  IFamilyMember,
  IFamilyValueItem,
} from '../../../../pages/common';
import type {
  CreateTreatmentPlanV3ClientSiblingPayload,
  CreateTreatmentPlanV3FamilyAdditionalInfoPayload,
  CreateTreatmentPlanV3FamilyMemberPayload,
  DeleteTreatmentPlanV3ClientSiblingPayload,
  DeleteTreatmentPlanV3FamilyAdditionalInfoPayload,
  DeleteTreatmentPlanV3FamilyMemberPayload,
  GetTreatmentPlanV3FamilyPagePayload,
  UpdateTreatmentPlanV3ClientSiblingPayload,
  UpdateTreatmentPlanV3FamilyMemberPayload,
  UpdateTreatmentPlanV3FamilyPayload,
} from './types';

export function createTreatmentPlanV3FamilyThunks() {
  return {
    getTreatmentPlanV3FamilyPage: getTreatmentPlanV3FamilyPage(),
    updateTreatmentPlanV3Family: updateTreatmentPlanV3Family(),
    createTreatmentPlanV3FamilyMember: createTreatmentPlanV3FamilyMember(),
    updateTreatmentPlanV3FamilyMember: updateTreatmentPlanV3FamilyMember(),
    deleteTreatmentPlanV3FamilyMember: deleteTreatmentPlanV3FamilyMember(),
    createTreatmentPlanV3ClientSibling: createTreatmentPlanV3ClientSibling(),
    updateTreatmentPlanV3ClientSibling: updateTreatmentPlanV3ClientSibling(),
    deleteTreatmentPlanV3ClientSibling: deleteTreatmentPlanV3ClientSibling(),
    createTreatmentPlanV3FamilyAdditionalInfo: createTreatmentPlanV3FamilyAdditionalInfo(),
    deleteTreatmentPlanV3FamilyAdditionalInfo: deleteTreatmentPlanV3FamilyAdditionalInfo(),
  };
}

function getTreatmentPlanV3FamilyPage() {
  return createAsyncThunk<IFamily, GetTreatmentPlanV3FamilyPagePayload>(
    'treatmentPlanV3/getTreatmentPlanV3FamilyPage',
    async ({ treatmentPlanId, getAccessTokenSilently }) =>
      fetchWrapper.get({
        url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/family/`,
        getAccessTokenSilently,
      }),
  );
}

function updateTreatmentPlanV3Family() {
  return createAsyncThunk<IFamily, UpdateTreatmentPlanV3FamilyPayload>(
    'treatmentPlanV3/updateTreatmentPlanV3Family',
    async ({ treatmentPlanId, data, getAccessTokenSilently }) =>
      fetchWrapper.patch(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/family/`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function createTreatmentPlanV3FamilyMember() {
  return createAsyncThunk<IFamilyMember, CreateTreatmentPlanV3FamilyMemberPayload>(
    'treatmentPlanV3/createTreatmentPlanV3FamilyMember',
    async ({ treatmentPlanId, data, getAccessTokenSilently }) =>
      fetchWrapper.post(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/family/members/`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function updateTreatmentPlanV3FamilyMember() {
  return createAsyncThunk<IFamilyMember, UpdateTreatmentPlanV3FamilyMemberPayload>(
    'treatmentPlanV3/updateTreatmentPlanV3FamilyMember',
    async ({ treatmentPlanId, memberId, data, getAccessTokenSilently }) =>
      fetchWrapper.patch(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/family/members/${memberId}/`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function deleteTreatmentPlanV3FamilyMember() {
  return createAsyncThunk<void, DeleteTreatmentPlanV3FamilyMemberPayload>(
    'treatmentPlanV3/deleteTreatmentPlanV3FamilyMember',
    async ({ treatmentPlanId, memberId, getAccessTokenSilently }) => {
      await fetchWrapper.delete({
        url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/family/members/${memberId}/`,
        getAccessTokenSilently,
      });
    },
  );
}

function createTreatmentPlanV3ClientSibling() {
  return createAsyncThunk<IClientSibling, CreateTreatmentPlanV3ClientSiblingPayload>(
    'treatmentPlanV3/createTreatmentPlanV3ClientSibling',
    async ({ treatmentPlanId, data, getAccessTokenSilently }) =>
      fetchWrapper.post(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/family/siblings/`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function updateTreatmentPlanV3ClientSibling() {
  return createAsyncThunk<IClientSibling, UpdateTreatmentPlanV3ClientSiblingPayload>(
    'treatmentPlanV3/updateTreatmentPlanV3ClientSibling',
    async ({ treatmentPlanId, siblingId, data, getAccessTokenSilently }) =>
      fetchWrapper.patch(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/family/siblings/${siblingId}/`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function deleteTreatmentPlanV3ClientSibling() {
  return createAsyncThunk<void, DeleteTreatmentPlanV3ClientSiblingPayload>(
    'treatmentPlanV3/deleteTreatmentPlanV3ClientSibling',
    async ({ treatmentPlanId, siblingId, getAccessTokenSilently }) => {
      await fetchWrapper.delete({
        url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/family/siblings/${siblingId}/`,
        getAccessTokenSilently,
      });
    },
  );
}

function createTreatmentPlanV3FamilyAdditionalInfo() {
  return createAsyncThunk<IFamilyValueItem, CreateTreatmentPlanV3FamilyAdditionalInfoPayload>(
    'treatmentPlanV3/createTreatmentPlanV3FamilyAdditionalInfo',
    async ({ treatmentPlanId, data, getAccessTokenSilently }) =>
      fetchWrapper.post(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/family/additional_info/`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function deleteTreatmentPlanV3FamilyAdditionalInfo() {
  return createAsyncThunk<void, DeleteTreatmentPlanV3FamilyAdditionalInfoPayload>(
    'treatmentPlanV3/deleteTreatmentPlanV3FamilyAdditionalInfo',
    async ({ treatmentPlanId, itemId, getAccessTokenSilently }) => {
      await fetchWrapper.delete({
        url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/family/additional_info/${itemId}/`,
        getAccessTokenSilently,
      });
    },
  );
}
